import { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalCompAdvanceUpdate } from "../../../components";
import Message from "../../../components/MessageModal";
import {
  useGetMembersQuery,
  useSearchMembersQuery,
  useDownloadMemberCSVMutation,
  useDownloadMemberPDFMutation,
} from "../../../store/admin/memberManagement/memberSlice";
import StaffMemberTable from "../modules/StaffMemberTable";
import { ViewMembersHeader } from "../../../utils/TableHeader";
import useMemberRegAction from "./useMemberRegAction";
import { toast } from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ViewMembers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [getModalName, setModalName] = useState();
  const [memberId, setMemeberId] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const { archiveMember, unSuspendMember, suspendMember } = useMemberRegAction();
  const [downloadCSV, { isLoading: isDownloadingCSV }] = useDownloadMemberCSVMutation();
  const [downloadPDF, { isLoading: isDownloadingPDF }] = useDownloadMemberPDFMutation();

  const handleArchiveMember = async (memberId) => {
    setModalName();
    if (!memberId) return;
    archiveMember(memberId);
  };

  const handleDownloadCSV = async () => {
    try {
      const toastId = toast.loading("Downloading CSV...");
      const blob = await downloadCSV({
        search: searchTerm,
        page: pageNumber,
      }).unwrap();

      // Check if blob is valid
      if (!blob || blob.size === 0) {
        toast.error("CSV file is empty. Please try again.", { id: toastId });
        return;
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `member-list-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("CSV downloaded successfully!", { id: toastId });
    } catch (error) {
      toast.error(error?.data?.message || "Failed to download CSV. Please try again.");
      console.error("CSV download error:", error);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const toastId = toast.loading("Generating PDF...");
      
      // Get the member data to export
      const membersToExport = searchTerm ? searchedMembers : allMembers;
      
      if (!membersToExport || membersToExport.length === 0) {
        toast.error("No members to export.", { id: toastId });
        return;
      }

      // Create new PDF document
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.text("Member List Report", 14, 20);
      
      // Add generation date
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
      
      if (searchTerm) {
        doc.text(`Search term: ${searchTerm}`, 14, 34);
      }

      // Prepare table data
      const tableData = membersToExport.map((member, index) => [
        index + 1,
        member.member_no || "N/A",
        member.first_name || "",
        member.last_name || "",
        member.email || "",
        member.phone || "",
        member.gender || "N/A",
        member.date_of_birth 
          ? new Date(member.date_of_birth).toLocaleDateString() 
          : "N/A",
        member.is_suspended 
          ? "Suspended" 
          : member.is_active 
            ? "Active" 
            : "Pending",
        member.branch || "N/A",
        member.created_at 
          ? new Date(member.created_at).toLocaleDateString() 
          : "N/A",
      ]);

      // Add table using autoTable
      autoTable(doc, {
        startY: searchTerm ? 40 : 34,
        head: [
          [
            "S/N",
            "Membership No",
            "First Name",
            "Last Name",
            "Email",
            "Phone",
            "Gender",
            "Date of Birth",
            "Status",
            "Branch",
            "Date Created",
          ],
        ],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: [56, 56, 56], // Dark grey header
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        columnStyles: {
          0: { cellWidth: 15 }, // S/N
          1: { cellWidth: 30 }, // Membership No
          2: { cellWidth: 30 }, // First Name
          3: { cellWidth: 30 }, // Last Name
          4: { cellWidth: 40 }, // Email
          5: { cellWidth: 30 }, // Phone
          6: { cellWidth: 20 }, // Gender
          7: { cellWidth: 30 }, // Date of Birth
          8: { cellWidth: 25 }, // Status
          9: { cellWidth: 35 }, // Branch
          10: { cellWidth: 30 }, // Date Created
        },
        margin: { top: 40 },
      });

      // Save the PDF
      const fileName = `member-list-${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);

      toast.success("PDF downloaded successfully!", { id: toastId });
    } catch (error) {
      toast.error("Failed to generate PDF. Please try again.");
      console.error("PDF generation error:", error);
    }
  };

  const { data: searchResult = {}, isFetching: isSearching } =
    useSearchMembersQuery(
      {
        page: pageNumber,
        search: searchTerm,
      },
      {
        skip: !searchTerm || searchTerm.trim() === "",
      }
    );
  const {
    data: membersQuerResult = {},
    isFetching: isFetchingData,
    error,
  } = useGetMembersQuery(pageNumber);

  const { allMembers, pagination } = useMemo(() => {
    const fetchedMembers = membersQuerResult?.members || [];
    const pagination = membersQuerResult?.pagination || {};
    const perPage = pagination.perPage || 20;
    const formattedMembers = fetchedMembers.map((item, index) => {
      return {
        ...item,
        name: item.first_name + " " + item.last_name,
        phone: item.phone,
        email: item.email,
        status: item.status,
        branch: item.branch?.name || "N/A",
        photo: item.photo,
        serialNumber: index + 1 + (pageNumber - 1) * perPage,
        id: item.id,
        is_active: item.is_activated,
        is_suspended: item.is_suspended || !item.is_activated,
      };
    });
    return { allMembers: formattedMembers, pagination };
  }, [membersQuerResult, pageNumber]);

  // const suspendStaffHandler = async (staffId) => {
  //   const credentials = {
  //     payload: {
  //       reason: "no reason",
  //     },
  //     staffId: staffId,
  //   };

  //   const response = await handleSuspendStaff(credentials);
  //   // console.log(response);
  //   const { message, status } = response?.data;
  //   // navigate("/super_admin/staff_registration/view_staff");
  //   // resetForm();

  //   if (!status) {
  //     toast.error(message);
  //   } else {
  //     toast.success(message);
  //     setModalName();
  //   }
  // };

  const onSearch = (value) => {
    setPageNumber(1);
    setSearchTerm(value);
  };
  const { searchedMembers, searchedPagination } = useMemo(() => {
    const fetchedMembers = searchResult?.members || [];
    const pagination = searchResult?.pagination || {};
    const perPage = pagination.perPage || 20;
    const formattedMembers = fetchedMembers.map((item, index) => {
      return {
        ...item,
        name: item.first_name + " " + item.last_name,
        phone: item.phone,
        email: item.email,
        status: item.status,
        branch: item.branch?.name || "N/A",
        photo: item.photo,
        serialNumber: index + 1 + (pageNumber - 1) * perPage,
        id: item.id,
        is_active: item.is_activated,
        is_suspended: item.is_suspended || !item.is_activated,
      };
    });
    return {
      searchedMembers: formattedMembers,
      searchedPagination: pagination,
    };
  }, [searchResult, pageNumber]);

  if (error && error.status === 401) {
    return navigate("/login");
  }
  const getTableDropdown = (row) => {
    const dropdown = [
      {
        name: "View profile",
        action: (data) => {
          navigate(`${location?.pathname}/${data?.id}`);
        },
      },
      {
        name: row.is_suspended ? "Unsuspend member" : "Suspend Member",
        action: (data) => {
          if (row.is_suspended) {
            setMemeberId(data?.id);
            setModalName("Unsuspend Member");
          } else {
            setMemeberId(data?.id);
            setModalName("Suspend Member");
            // navigate(
            //   `${location?.pathname}/${data?.id}/suspend?name=${data.name}`
            // );
          }
        },
      },
      // {
      //   name: "Change branch",
      //   // disabled: true,
      //   action: (data) => {
      //     navigate(`${location?.pathname}/${data?.id}/change_branch`);
      //   },
      // },
      // {
      //   name: "Edit Member",
      //   action: (data) => {
      //     navigate(
      //       `${location?.pathname}/${data?.id}/update?name=${data.name}`
      //     );
      //   },
      // },
      {
        name: "Archive Member",
        action: (data) => {
          setMemeberId(data?.id);
          setModalName("Archive Member");
        },
      },
    ];
    return dropdown;
  };
  const useModal = [
    {
      name: "Archive Member",
      component: (
        <Message
          title={"Archive Member"}
          desc={"Do you want to archive this member account?"}
          onBack={() => setModalName()}
          onProceed={() => handleArchiveMember(memberId)}
        />
      ),
    },
    {
      name: "Unsuspend Member",
      component: (
        <Message
          title={"Unsuspend Member"}
          desc={"Do you want to unsuspend this member account?"}
          onBack={() => setModalName()}
          onProceed={() => {
            setModalName();
            unSuspendMember(memberId);
          }}
        />
      ),
    },
    {
      name: "Suspend Member",
      component: (
        <Message
          title={"Suspend Member"}
          desc={"Do you want to suspend this member account?"}
          onBack={() => setModalName()}
          onProceed={() => {
            setModalName();
            // suspendMember(memberId, "no reason");
            suspendMember({ memberId, reason: "no reason" });
          }}
        />
      ),
    },
  ];

  return (
    <>
      <StaffMemberTable
        title={"View Members"}
        tableFor="members"
        tableData={searchTerm ? searchedMembers : allMembers}
        isFetchingData={isFetchingData || isSearching}
        tableColumns={ViewMembersHeader(getTableDropdown)}
        addNew={() => navigate("/super_admin/member_management")}
        pagination={searchTerm ? searchedPagination : pagination}
        onPageChange={setPageNumber}
        onSearch={onSearch}
        onDownloadCSV={handleDownloadCSV}
        onDownloadPDF={handleDownloadPDF}
        isDownloadingCSV={isDownloadingCSV}
        isDownloadingPDF={isDownloadingPDF}
        searchTerm={searchTerm}
        pageNumber={pageNumber}
      />
      <ModalCompAdvanceUpdate
        centered
        arrayComp={useModal}
        pageName={getModalName}
        handleClose={() => setModalName()}
      />
    </>
  );
}
